import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { LoginService } from 'src/app/modules/login/login.service';
import { formatElapsedMs } from '../_helpers/platform-health-metrics';
import { PlatformHealthService } from '../_services/platform-health.service';
import {
    AppInsightsBlock, BlockStatus, DatabaseBlock, DatabaseUsersBlock, MetricSeries, PlatformBlock,
    RunningRequest, SessionsBlock
} from '../_types/platform-health';

@Component({
    selector: 'app-platform-health-dashboard',
    templateUrl: './platform-health-dashboard.component.html',
    styleUrls: ['./platform-health-dashboard.component.scss'],
    standalone: false
})
export class PlatformHealthDashboardComponent implements OnInit, OnDestroy {

    public static readonly PERMISSION_INFRASTRUCTURE = 'PLATFORM_HEALTH.INFRASTRUCTURE';
    public static readonly PERMISSION_DATABASE = 'PLATFORM_HEALTH.DATABASE';
    public static readonly PERMISSION_KILL_SESSION = 'PLATFORM_HEALTH.KILL_SESSION';

    public canSeeInfrastructure = false;
    public canSeeDatabase = false;
    public canKillSession = false;

    public period = '1h';
    public periodOptions = [
        { value: '1h', name: 'Última hora' },
        { value: '6h', name: 'Últimas 6 horas' },
        { value: '24h', name: 'Últimas 24 horas' },
        { value: '7d', name: 'Últimos 7 días' },
        { value: '30d', name: 'Últimos 30 días' }
    ];

    /// Refresco automático, aparte del botón «Actualizar»: "No" por defecto a propósito, el mismo
    /// motivo que justificaba no tener ninguno antes. Es independiente de "period": cambiar uno no
    /// afecta al otro, ni en la plantilla ni aquí.
    public autoRefreshInterval: number | null = null;
    public autoRefreshOptions = [
        { value: null, name: 'No' },
        { value: 60000, name: '1 min' },
        { value: 300000, name: '5 min' }
    ];

    private autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

    public platform: PlatformBlock | null = null;
    public database: DatabaseBlock | null = null;
    public appInsights: AppInsightsBlock | null = null;
    public sessions: SessionsBlock | null = null;
    public users: DatabaseUsersBlock | null = null;

    public loading = false;
    public refreshedAt: Date | null = null;

    /// Fuera del contador de generación de refresh(): la tabla de usuarios no comparte periodo con
    /// el resto del panel (siempre es una foto del momento, igual que sessions), así que se carga y
    /// se recarga por su cuenta y no cuenta como una de las peticiones de "pending".
    public usersLoading = false;

    /// Contador de generación: cada refresh() lo incrementa y captura su propio valor. 'next' y
    /// 'settle' de una ronda vieja lo comparan contra el valor vigente antes de tocar nada; si no
    /// coincide, esa ronda quedó invalidada por una más nueva (cambio de periodo o doble clic en
    /// «Actualizar») y se ignora entera, aunque su petición HTTP siga en vuelo y acabe llegando.
    private generation = 0;

    public trafficChart: any = null;
    public latencyChart: any = null;
    public appServiceChart: any = null;
    public threadsChart: any = null;
    public databaseChart: any = null;
    public poolChart: any = null;

    private static readonly COLORS = ['#3b82f6', '#ef4444', '#16a34a', '#f59e0b', '#8b5cf6'];

    /// Opciones comunes a cuatro de las seis gráficas (latencia, hilos, base de datos y pool): un
    /// único objeto que se comparte tal cual entre ellas, sin variantes. El eje X se ve en las seis
    /// (no se oculta en ninguna); cada una tiene su propio título h3 y no pretenden leerse como una
    /// sola gráfica continua.
    public chartOptions: any = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom' } },
        scales: {
            x: { ticks: { maxTicksLimit: 12 } },
            y: { beginAtZero: true }
        }
    };

    /// Tráfico y errores necesita su propio objeto: los 5xx comparten eje con las peticiones y, en
    /// una escala de miles, un puñado de errores queda pegado al cero. 'y1' es un eje propio a la
    /// derecha, sin rejilla sobre el área de la gráfica para no ensuciarla con dos cuadrículas.
    public trafficChartOptions: any = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom' } },
        scales: {
            x: { ticks: { maxTicksLimit: 12 } },
            y: { beginAtZero: true },
            y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } }
        }
    };

    /// App Service: InstanceCount va de 1 a 3 y comparte escala con CPU/memoria (0-100), así que
    /// también necesita eje propio a la derecha para poder ver si un pico coincide con un escalado.
    public appServiceChartOptions: any = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom' } },
        scales: {
            x: { ticks: { maxTicksLimit: 12 } },
            y: { beginAtZero: true },
            y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } }
        }
    };

    constructor(private platformHealthService: PlatformHealthService, private loginService: LoginService,
        private topbarService: TopbarService, private confirmationService: ConfirmationService,
        private messageService: MessageService) { }

    public ngOnInit(): void {
        //sin esto el título y la miga de pan se pierden al recargar: la miga sale del título
        //que fija cada pantalla, no de la ruta
        this.topbarService.setTitle('Salud de la plataforma');

        this.canSeeInfrastructure = this.loginService.hasPermission(PlatformHealthDashboardComponent.PERMISSION_INFRASTRUCTURE);
        this.canSeeDatabase = this.loginService.hasPermission(PlatformHealthDashboardComponent.PERMISSION_DATABASE);
        //solo estético: oculta el botón de cortar sesión en la tabla. El control de acceso real está
        //en el servidor, que vuelve a comprobar este mismo permiso (y el login protegido) antes de matar nada.
        this.canKillSession = this.loginService.hasPermission(PlatformHealthDashboardComponent.PERMISSION_KILL_SESSION);
        this.refresh();
        this.loadUsers();
    }

    /// Para el temporizador al destruir el componente: si no, seguiría llamando a refresh() (y por
    /// tanto pidiendo a Azure) después de que el usuario haya navegado a otra pantalla, sin que se
    /// vea ni se entienda desde fuera.
    public ngOnDestroy(): void {
        this.clearAutoRefreshTimer();
    }

    /// Al cambiar el desplegable de refresco automático: se reinicia el temporizador desde cero con
    /// el intervalo nuevo (o se para del todo si se elige "No"). No toca 'period' ni llama a
    /// refresh(): cambiar esta opción no es, por sí solo, una razón para refrescar ya.
    public onAutoRefreshChange(): void {
        this.restartAutoRefreshTimer();
    }

    private restartAutoRefreshTimer(): void {
        this.clearAutoRefreshTimer();
        if (this.autoRefreshInterval) {
            this.autoRefreshTimer = setInterval(() => {
                // Si ya hay una ronda en vuelo, se salta este tic en lugar de encadenar otra: el
                // contador de generación de refresh() ya evita pintar datos de una ronda vieja, pero
                // encadenar peticiones sin necesidad es gastar llamadas a Azure de más.
                if (this.loading) return;
                this.refresh();
            }, this.autoRefreshInterval);
        }
    }

    private clearAutoRefreshTimer(): void {
        if (this.autoRefreshTimer !== null) {
            clearInterval(this.autoRefreshTimer);
            this.autoRefreshTimer = null;
        }
    }

    public refresh(): void {
        // Generación de esta ronda: se captura ya, antes de lanzar nada, para que cualquier next/
        // error/settle que llegue después de una ronda MÁS NUEVA (this.generation ya incrementado
        // de nuevo) se reconozca a sí mismo como obsoleto y no toque el estado del componente.
        const gen = ++this.generation;

        // Nº de peticiones que se van a lanzar de verdad, según los permisos: si solo hay uno de
        // los dos grupos, "loading" y "refreshedAt" no pueden esperar a las cuatro.
        let pending = (this.canSeeInfrastructure ? 2 : 0) + (this.canSeeDatabase ? 2 : 0);

        if (pending === 0) {
            this.refreshedAt = new Date();
            return;
        }

        this.loading = true;

        // Limpiar el periodo anterior: si no, la pantalla seguiría enseñando indicadores, gráficas
        // y tablas del periodo YA sustituido hasta que lleguen las cuatro respuestas nuevas, sin
        // ninguna señal de que son datos viejos. En un panel de diagnóstico eso es peligroso: un
        // pico del periodo anterior se podría leer como del periodo recién elegido.
        this.platform = null;
        this.database = null;
        this.appInsights = null;
        this.sessions = null;
        this.trafficChart = null;
        this.latencyChart = null;
        this.appServiceChart = null;
        this.threadsChart = null;
        this.databaseChart = null;
        this.poolChart = null;

        // Se llama al terminar cada suscripción (éxito o error). Cuando ya no queda ninguna en
        // vuelo, refreshedAt refleja la hora real de los datos, no la de la petición.
        //
        // Las gráficas se reconstruyen aquí, una sola vez al final, y no en cada next: individual.
        // La de latencia mezcla datos de dos peticiones (platform y appInsights) que llegan en
        // paralelo y sin orden garantizado; reconstruir tras cada una pintaría una versión sin
        // percentiles primero y luego la sustituiría, un parpadeo que además arma mal el usuario si
        // llega justo cuando está leyendo el pico. Esperar a que "pending" llegue a 0 evita ese
        // estado intermedio y coincide con el mismo instante en que ya se marca refreshedAt.
        const settle = (): void => {
            if (gen !== this.generation) return;
            pending--;
            if (pending === 0) {
                this.loading = false;
                this.refreshedAt = new Date();
                this.buildCharts();
            }
        };

        //cada bloque por su cuenta: que Application Insights falle no puede dejar sin plataforma
        if (this.canSeeInfrastructure) {
            this.platformHealthService.getPlatform(this.period).subscribe({
                next: (b) => { if (gen !== this.generation) return; this.platform = b; settle(); },
                error: (err: HttpErrorResponse) => { if (gen !== this.generation) return; this.platform = this.platformFailure(err); settle(); }
            });
            this.platformHealthService.getAppInsights(this.period).subscribe({
                next: (b) => { if (gen !== this.generation) return; this.appInsights = b; settle(); },
                error: (err: HttpErrorResponse) => { if (gen !== this.generation) return; this.appInsights = this.appInsightsFailure(err); settle(); }
            });
        }

        if (this.canSeeDatabase) {
            this.platformHealthService.getDatabase(this.period).subscribe({
                next: (b) => { if (gen !== this.generation) return; this.database = b; settle(); },
                error: (err: HttpErrorResponse) => { if (gen !== this.generation) return; this.database = this.databaseFailure(err); settle(); }
            });
            this.platformHealthService.getSessions().subscribe({
                next: (b) => { if (gen !== this.generation) return; this.sessions = b; settle(); },
                error: (err: HttpErrorResponse) => { if (gen !== this.generation) return; this.sessions = this.sessionsFailure(err); settle(); }
            });
        }
    }

    /// Usuarios de la base y sus sesiones abiertas: deliberadamente fuera del contador de generación y
    /// del "pending" de refresh(). Es una foto del momento como sessions, pero no comparte periodo con
    /// el resto del panel, así que se carga por su cuenta al entrar y cada vez que se pulsa
    /// "Actualizar" (ver la plantilla), y también se recarga sola tras cortar una sesión con éxito.
    public loadUsers(): void {
        if (!this.canSeeDatabase) return;

        this.usersLoading = true;
        this.platformHealthService.getDatabaseUsers().subscribe({
            next: (b) => { this.usersLoading = false; this.users = b; },
            error: (err: HttpErrorResponse) => { this.usersLoading = false; this.users = this.usersFailure(err); }
        });
    }

    /// Clic en "Cortar sesión" de la tabla de consultas en ejecución (evento emitido por
    /// app-platform-health-database). Pide confirmación explícita con el login, la aplicación y el
    /// tiempo transcurrido de la sesión, tal y como se vio en la tabla en ese instante: el servidor es
    /// quien vuelve a comprobar, contra el estado real de la base, a qué login pertenece esa sesión en
    /// el momento de matar, así que un dato desactualizado aquí (la sesión se recicló mientras tanto)
    /// no compromete la seguridad de la operación, solo el texto del diálogo.
    public onKillSession(request: RunningRequest): void {
        const elapsed = formatElapsedMs(request.elapsedMs);
        this.confirmationService.confirm({
            message: `Se cortará la sesión ${request.sessionId}, del login "${request.loginName}" y la aplicación "${request.programName}", con ${elapsed} transcurridos. Esta acción no se puede deshacer.`,
            header: 'Cortar sesión',
            acceptLabel: 'Cortar sesión',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.platformHealthService.killSession(request.sessionId).subscribe({
                    next: (result) => {
                        this.messageService.add({
                            closable: true,
                            severity: result.success ? 'success' : 'warn',
                            summary: result.success ? 'Sesión cortada' : 'No se ha cortado la sesión',
                            detail: result.message
                        });
                        //se recargan las sesiones tanto si se ha cortado como si no: en el caso de
                        //fallo (login protegido, sesión ya inexistente...) la tabla sigue siendo la
                        //foto más reciente que se puede enseñar, y en el de éxito es la única forma
                        //de ver el efecto.
                        this.reloadSessions();
                    },
                    error: (err: HttpErrorResponse) => {
                        this.messageService.add({
                            closable: true,
                            severity: 'error',
                            summary: 'No se ha podido cortar la sesión',
                            detail: `No se pudo consultar al servidor (HTTP ${err?.status || 'desconocido'}).`
                        });
                    }
                });
            }
        });
    }

    /// Solo la tabla de sesiones, sin tocar platform/database/appInsights ni el contador de
    /// generación de refresh(): recargar todo el panel tras un solo cortado sería desproporcionado y
    /// arrastraría el resto de peticiones (Azure incluido) que no tienen nada que ver con la acción.
    private reloadSessions(): void {
        if (!this.canSeeDatabase) return;

        this.platformHealthService.getSessions().subscribe({
            next: (b) => { this.sessions = b; },
            error: (err: HttpErrorResponse) => { this.sessions = this.sessionsFailure(err); }
        });

        //la tabla de usuarios cuenta sesiones por usuario, así que tras cortar una se queda con el
        //recuento viejo: hay que recargarla también o las dos tablas se contradicen en pantalla
        this.loadUsers();
    }

    /// Motivo legible para un bloque cuya petición ha fallado del todo (500, timeout, red caída):
    /// deja claro que no hemos podido ni preguntar, para no confundirlo con "Azure respondió y no
    /// hay datos".
    private requestFailureStatus(err: HttpErrorResponse): BlockStatus {
        const httpStatus = err?.status || 'desconocido';
        return {
            status: 'unavailable',
            reason: `No se pudo consultar al servidor (HTTP ${httpStatus}): ha fallado la petición, no es que Azure no tenga datos.`
        };
    }

    private platformFailure(err: HttpErrorResponse): PlatformBlock {
        return { status: this.requestFailureStatus(err), fromUtc: '', toUtc: '', granularity: '', series: [] };
    }

    private databaseFailure(err: HttpErrorResponse): DatabaseBlock {
        return { status: this.requestFailureStatus(err), databaseName: null, series: [], poolSeries: [], poolName: null };
    }

    private appInsightsFailure(err: HttpErrorResponse): AppInsightsBlock {
        return { status: this.requestFailureStatus(err), latency: [], topOperations: [], dependencies: [] };
    }

    private sessionsFailure(err: HttpErrorResponse): SessionsBlock {
        return { status: this.requestFailureStatus(err), capturedAtUtc: '', groups: [], running: [] };
    }

    private usersFailure(err: HttpErrorResponse): DatabaseUsersBlock {
        return { status: this.requestFailureStatus(err), capturedAtUtc: '', users: [] };
    }

    /// Una gráfica a partir de las series que se le pidan. 'field' elige qué agregación se pinta,
    /// porque para CPU interesa el máximo y para peticiones el total. Si el bloque llegó degradado
    /// (status 'unavailable', series === []) o directamente no ha llegado (series === undefined),
    /// 'series' está vacío o no trae ninguno de los nombres pedidos, y se devuelve null: la
    /// plantilla ya no pinta nada sin datos reales gracias al *ngIf sobre el propio chart.
    ///
    /// Tampoco basta con que la serie exista con el nombre pedido: si NINGÚN dataset resultante
    /// tiene un solo valor no nulo (p. ej. el pool está presente pero sin puntos en ese periodo),
    /// se devuelve null también. Sin este chequeo, buildChart devolvía una gráfica válida con 200
    /// puntos a null: chart.js la pinta como una línea plana en cero, que se lee como "todo va bien"
    /// en vez de "no hay datos", justo la inversión de diagnóstico que el bloque quiere evitar.
    private buildChart(series: MetricSeries[] | undefined, names: string[], field: 'average' | 'maximum' | 'total', labels?: string[]): any {
        if (!series?.length) return null;

        const first = series.find((s) => names.includes(s.name));
        if (!first) return null;

        const datasets = names
            .map((name, i) => {
                const found = series.find((s) => s.name === name);
                if (!found) return null;
                return {
                    label: labels?.[i] ?? name,
                    data: found.points.map((p) => p[field]),
                    borderColor: PlatformHealthDashboardComponent.COLORS[i % PlatformHealthDashboardComponent.COLORS.length],
                    backgroundColor: PlatformHealthDashboardComponent.COLORS[i % PlatformHealthDashboardComponent.COLORS.length],
                    borderWidth: 2,
                    pointRadius: 0,
                    tension: 0.2
                };
            })
            .filter((d) => d !== null);

        const hasAnyValue = datasets.some((d: any) => (d.data as (number | null)[]).some((v) => v !== null));
        if (!hasAnyValue) return null;

        return {
            labels: first.points.map((p) => new Date(p.timestamp).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })),
            datasets
        };
    }

    /// Tiempo de respuesta: el medio y el máximo de plataforma, y encima los percentiles de
    /// Application Insights cuando ese bloque responde. Los percentiles vienen en milisegundos y la
    /// métrica de plataforma en segundos, así que se convierten para que compartan escala.
    private buildLatencyChart(): any {
        const base = this.buildChart(this.platform?.series, ['HttpResponseTime'], 'maximum', ['Máximo de plataforma (s)']);
        if (!base) return null;

        const extra = this.buildChart(this.platform?.series, ['HttpResponseTime'], 'average', ['Medio de plataforma (s)']);
        if (extra?.datasets?.length) {
            extra.datasets[0].borderColor = '#94a3b8';
            extra.datasets[0].backgroundColor = '#94a3b8';
            base.datasets.push(extra.datasets[0]);
        }

        //los percentiles solo se superponen si App Insights trae los mismos puntos que la serie de
        //plataforma; con longitudes distintas el eje X mentiría y es mejor no pintarlos. Si el
        //bloque de App Insights llegó degradado, appInsights?.status?.status !== 'ok' y esta rama
        //se salta entera sin tocar 'latency' (que en ese caso es []).
        const latency = this.appInsights?.latency;
        if (this.appInsights?.status?.status === 'ok' && latency && latency.length === base.labels.length) {
            const percentiles: [string, 'p50' | 'p95' | 'p99', string][] = [
                ['p50 (s)', 'p50', '#16a34a'],
                ['p95 (s)', 'p95', '#f59e0b'],
                ['p99 (s)', 'p99', '#ef4444']
            ];
            for (const [label, field, color] of percentiles) {
                base.datasets.push({
                    label,
                    data: latency.map((p) => p[field] / 1000),
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 2,
                    borderDash: [4, 3],
                    pointRadius: 0,
                    tension: 0.2
                });
            }
        }

        return base;
    }

    /// Reconstruye las seis gráficas a partir de lo que haya en platform/database/appInsights en
    /// ese momento. Se puede llamar con cualquier combinación de bloques a null, vacíos o
    /// 'unavailable': buildChart() y buildLatencyChart() devuelven null en cuanto falta lo que
    /// necesitan, nunca lanzan.
    private buildCharts(): void {
        this.trafficChart = this.buildChart(this.platform?.series, ['Requests', 'Http5xx'], 'total', ['Peticiones', 'Errores 5xx']);
        //los 5xx comparten magnitud con nada: en una escala de miles de peticiones, unos pocos
        //errores quedan pegados al cero. Eje propio a la derecha (ver trafficChartOptions).
        this.setYAxis(this.trafficChart, 'Errores 5xx', 'y1');

        this.latencyChart = this.buildLatencyChart();

        this.appServiceChart = this.buildChart(this.platform?.series, ['CpuPercentage', 'MemoryPercentage', 'InstanceCount'], 'maximum', ['CPU %', 'Memoria %', 'Instancias']);
        //InstanceCount va de 1 a 3 y comparte escala con CPU/memoria (0-100): pegada al suelo.
        //Eje propio a la derecha y en escalones, para que un escalado se vea como tal.
        this.setYAxis(this.appServiceChart, 'Instancias', 'y1', { stepped: true });

        //la gráfica que hace de cola: en Linux HttpQueueLength es una métrica de IIS y sale 0
        //siempre, así que lo que delata el encolamiento es el crecimiento de hilos del ThreadPool
        this.threadsChart = this.buildChart(this.platform?.series, ['Threads'], 'maximum', ['Hilos']);

        const dbNames = ['cpu_percent', 'physical_data_read_percent', 'log_write_percent', 'workers_percent', 'sessions_percent'];
        const dbLabels = ['CPU %', 'Data IO %', 'Log write %', 'Workers %', 'Sesiones %'];
        //media y no maximo: el maximo del intervalo convierte cualquier pico puntual en una
        //meseta y hace ilegible la tendencia. El pico no se pierde de vista porque sigue estando
        //en el indicador "CPU max. de la BD" de la fila de arriba.
        this.databaseChart = this.buildChart(this.database?.series, dbNames, 'average', dbLabels);
        this.poolChart = this.buildChart(this.database?.poolSeries, dbNames, 'average', dbLabels);
    }

    /// Ajusta el dataset de una gráfica ya construida (por su 'label') para que use un eje
    /// secundario y, opcionalmente, otras propiedades de chart.js (p. ej. stepped). No hace nada
    /// si la gráfica es null o el dataset no está: mantiene buildChart() genérico y ajena a estos
    /// casos particulares.
    private setYAxis(chart: any, datasetLabel: string, yAxisID: string, extra?: Record<string, any>): void {
        const dataset = chart?.datasets?.find((d: any) => d.label === datasetLabel);
        if (!dataset) return;
        dataset.yAxisID = yAxisID;
        Object.assign(dataset, extra ?? {});
    }
}
